import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import {
  Vote,
  Plus,
  Trash2,
  Edit,
  CheckCircle,
  Clock,
  Users,
  BarChart3,
  X
} from 'lucide-react';

const AdminPolls = () => {
  const { token } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    description: '',
    options: ['', ''],
    allow_multiple: false,
    ends_at: ''
  });

  useEffect(() => {
    fetchPolls();
  }, [token]);

  const fetchPolls = async () => {
    try {
      const response = await axios.get(`${API}/polls?active_only=false`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Get results for each poll
      const pollsWithResults = await Promise.all(
        response.data.map(async (poll) => {
          try {
            const resultRes = await axios.get(`${API}/polls/${poll.id}`);
            return { ...poll, ...resultRes.data };
          } catch {
            return poll;
          }
        })
      );
      
      setPolls(pollsWithResults);
    } catch (error) {
      console.error('Failed to fetch polls:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.question.trim()) {
      toast.error('Please enter a question');
      return;
    }
    
    const validOptions = formData.options.filter(opt => opt.trim());
    if (validOptions.length < 2) {
      toast.error('Please provide at least 2 options');
      return;
    }

    try {
      if (editingPoll) {
        await axios.patch(`${API}/admin/polls/${editingPoll.id}`, {
          ...formData,
          options: validOptions
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Poll updated');
      } else {
        await axios.post(`${API}/admin/polls`, {
          ...formData,
          options: validOptions
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Poll created');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchPolls();
    } catch (error) {
      toast.error('Failed to save poll');
    }
  };

  const handleDelete = async (pollId) => {
    if (!window.confirm('Are you sure you want to delete this poll?')) return;
    
    try {
      await axios.delete(`${API}/admin/polls/${pollId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Poll deleted');
      fetchPolls();
    } catch (error) {
      toast.error('Failed to delete poll');
    }
  };

  const togglePollStatus = async (poll) => {
    try {
      await axios.patch(`${API}/admin/polls/${poll.id}`, {
        is_active: !poll.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(poll.is_active ? 'Poll deactivated' : 'Poll activated');
      fetchPolls();
    } catch (error) {
      toast.error('Failed to update poll');
    }
  };

  const resetForm = () => {
    setFormData({
      question: '',
      description: '',
      options: ['', ''],
      allow_multiple: false,
      ends_at: ''
    });
    setEditingPoll(null);
  };

  const openEditDialog = (poll) => {
    setEditingPoll(poll);
    setFormData({
      question: poll.question,
      description: poll.description || '',
      options: poll.options || ['', ''],
      allow_multiple: poll.allow_multiple || false,
      ends_at: poll.ends_at || ''
    });
    setDialogOpen(true);
  };

  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    if (formData.options.length <= 2) return;
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };

  const updateOption = (index, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin w-8 h-8 border-4 border-plp-blue border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <Vote className="w-8 h-8 text-plp-gold" />
            Community Polls
          </h1>
          <p className="text-slate-600 mt-1">Create and manage community polls and surveys</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-plp-gold hover:bg-plp-gold/90 text-plp-blue" data-testid="create-poll-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Poll
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPoll ? 'Edit Poll' : 'Create New Poll'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Question *</Label>
                <Input
                  value={formData.question}
                  onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
                  placeholder="What would you like to ask?"
                  data-testid="poll-question-input"
                />
              </div>
              
              <div>
                <Label>Description (optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Provide more context..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label className="mb-2 block">Options *</Label>
                <div className="space-y-2">
                  {formData.options.map((option, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        value={option}
                        onChange={(e) => updateOption(idx, e.target.value)}
                        placeholder={`Option ${idx + 1}`}
                        data-testid={`poll-option-${idx}`}
                      />
                      {formData.options.length > 2 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeOption(idx)}
                          className="text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={addOption}
                  className="mt-2 w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Option
                </Button>
              </div>
              
              <div className="flex items-center justify-between">
                <Label>Allow multiple selections</Label>
                <Switch
                  checked={formData.allow_multiple}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_multiple: checked }))}
                />
              </div>
              
              <div>
                <Label>End Date (optional)</Label>
                <Input
                  type="datetime-local"
                  value={formData.ends_at}
                  onChange={(e) => setFormData(prev => ({ ...prev, ends_at: e.target.value }))}
                />
              </div>
              
              <Button type="submit" className="w-full bg-plp-blue hover:bg-plp-blue/90" data-testid="save-poll-btn">
                {editingPoll ? 'Update Poll' : 'Create Poll'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {polls.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Vote className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-600">No polls yet</h3>
            <p className="text-slate-500 mt-1">Create your first community poll to gather feedback</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {polls.map((poll) => {
            const totalVotes = poll.results?.total_votes || 0;
            
            return (
              <Card key={poll.id} className={`${!poll.is_active ? 'opacity-60' : ''}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {poll.is_active ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs rounded-full flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Active
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Closed
                          </span>
                        )}
                      </div>
                      <CardTitle className="text-lg">{poll.question}</CardTitle>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(poll)}
                        className="text-slate-500 hover:text-plp-blue"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(poll.id)}
                        className="text-slate-500 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  {poll.description && (
                    <p className="text-sm text-slate-500 mt-1">{poll.description}</p>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-4">
                    {(poll.options || []).map((option, idx) => {
                      const voteCount = poll.results?.vote_counts?.[idx] || 0;
                      const percentage = totalVotes > 0 ? ((voteCount / totalVotes) * 100).toFixed(1) : 0;
                      
                      return (
                        <div key={`${poll.id}-option-${idx}`} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-700">{option}</span>
                            <span className="text-slate-500">{voteCount} ({percentage}%)</span>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-plp-blue transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex items-center justify-between pt-3 border-t">
                    <span className="text-sm text-slate-500 flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      {totalVotes} votes
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => togglePollStatus(poll)}
                      className={poll.is_active ? 'text-red-500' : 'text-emerald-500'}
                    >
                      {poll.is_active ? 'Close Poll' : 'Reopen Poll'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminPolls;
